/* Herbruikbare popover: lijst campagnes + enrol leads/kandidaten (ook gebruikt door Shortlists). */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Link } from "react-router-dom";
import { X, Loader2, ArrowRight, Megaphone } from "lucide-react";

// ═════════════════════════════════════════════════════════════════════════
// AddToCampaignPopover — reusable popover that lists campaigns and enrols the
// given entities (leads or candidates) into the selected one. Exported for
// reuse from Recruitment side as well.
// ═════════════════════════════════════════════════════════════════════════
export function AddToCampaignPopover({ entityType = "lead", entityIds = [], onEnrolled, testidPrefix = "atc", buttonLabel }) {
    const [open, setOpen] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [enrolling, setEnrolling] = useState(null);
    const desiredType = entityType === "lead" ? "sales" : "recruitment";

    const openPopover = async () => {
        setOpen(true);
        setLoading(true);
        try {
            const r = await api.get(`/campaigns?type=${desiredType}`);
            setCampaigns((r.data || []).filter((c) => c.status !== "completed"));
        } catch (e) { toast.error(i18n.t("pipeline_sales.campaigns_load_failed")); }
        finally { setLoading(false); }
    };

    const enrol = async (campaignId) => {
        setEnrolling(campaignId);
        try {
            const payload = entityType === "lead"
                ? { lead_ids: entityIds }
                : { candidate_ids: entityIds };
            const r = await api.post(`/campaigns/${campaignId}/enroll`, payload);
            toast.success(r.data.summary);
            setOpen(false);
            onEnrolled?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("pipeline_sales.enrol_failed"));
        } finally { setEnrolling(null); }
    };

    const disabled = entityIds.length === 0;

    return (
        <div className="relative inline-block"
            onKeyDown={(e) => {
                // Escape sluit éérst de popover — niet de omliggende drawer.
                if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); }
            }}>
            <button onClick={openPopover} disabled={disabled} data-testid={`${testidPrefix}-open`}
                className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--jr-brand)]/40 bg-[var(--jr-brand)]/10 text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/20 disabled:opacity-50">
                <Megaphone size={12} /> {buttonLabel || i18n.t("pipeline_sales.add_to_campaign")}
            </button>
            {open && (
                <>
                    {/* backdrop */}
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 z-50 w-80 rounded-xl border border-white/10 bg-[var(--jr-surface)] shadow-2xl overflow-hidden"
                        data-testid={`${testidPrefix}-popover`}>
                        <div className="p-3 border-b border-white/8 flex items-center justify-between">
                            <span className="text-xs font-medium text-white">Kies campagne</span>
                            <button onClick={() => setOpen(false)} className="text-[var(--jr-text-3)] hover:text-white"><X size={14} /></button>
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                            {loading ? (
                                <div className="p-4 text-center"><Loader2 className="animate-spin inline" size={16} /></div>
                            ) : campaigns.length === 0 ? (
                                <div className="p-4 text-xs text-[var(--jr-text-3)] text-center">
                                    Nog geen {desiredType} campagnes.
                                    <Link to="/app/campaigns" className="block mt-2 text-[var(--jr-brand)] hover:underline">
                                        Maak er één aan →
                                    </Link>
                                </div>
                            ) : (
                                <div className="divide-y divide-white/6">
                                    {campaigns.map((c) => (
                                        <button key={c.id} onClick={() => enrol(c.id)} disabled={enrolling === c.id}
                                            data-testid={`${testidPrefix}-select-${c.id}`}
                                            className="w-full text-left p-3 hover:bg-white/[0.03] disabled:opacity-50 flex items-center gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-white truncate">{c.name}</div>
                                                <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5">
                                                    {(c.steps || []).length} steps · {c.stats?.enrolled || 0} enrolled · <em>{c.status}</em>
                                                </div>
                                            </div>
                                            {enrolling === c.id ? <Loader2 className="animate-spin" size={12} /> : <ArrowRight size={12} className="text-[var(--jr-text-3)]" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="p-2 border-t border-white/8">
                            <Link to="/app/campaigns"
                                className="block text-center text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)] hover:underline py-1">
                                + Beheer campagnes
                            </Link>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
